import {
    BackendClient,
    BlobUploadType,
    RequestParameters,
} from './backend-client';
import { handleError } from '~/utils/errors';

export const DEFAULT_POLL_INTERVAL_MS = 10000;
export const DEFAULT_TIMEOUT_MS = 500000;

/**
 * Shared base for task-family v2 backend clients. Owns the pieces
 * every family has in common:
 *
 * - `/v2/images` dataset endpoints (create dataset, upload slice) are
 *   task-family-agnostic; the backend keys them by ID, not by task
 *   type, so the same methods serve registration, composite-CBCT and
 *   any future family.
 * - Task lifecycle endpoints (`/v2/{taskPathSegment}/{task_id}` and
 *   its `/results/{result_id}` child) are structurally identical
 *   across families — same polling loop, same result-blob download,
 *   same delete — so they live here, parameterised by the
 *   `taskPathSegment` each subclass supplies.
 *
 * Subclasses add only the family-specific task-creation method,
 * because that's the one place where the request body differs (reg
 * takes a transformation matrix; composite-CBCT does not).
 */
export abstract class BackendV2Client extends BackendClient {
    /**
     * URL segment that identifies this task family, plugged into
     * `/v2/{segment}/{task_id}` etc. `'reg'` for registration,
     * `'composite_cbct'` for composite CBCT.
     */
    protected abstract get taskPathSegment(): string;

    /**
     * Creates a new image dataset on the backend and returns the
     * response JSON (which carries the dataset `id` used as a
     * reference in the task-creation payload).
     */
    public async createImageDataset(
        request: RequestParameters
    ): Promise<unknown> {
        try {
            const params: Record<string, string> = {};
            params.sessionId = request.sessionId ?? this.sessionID;

            const url = this.envelopeURL('v2/images', params);

            const headers = {
                ...(await this.defaultHeaders()),
                'Content-Type': 'application/json',
            };

            const body = JSON.stringify({
                expected_amount: Number(request.nExpectedFiles),
            });

            const response = await fetch(url, {
                method: 'POST',
                headers,
                body,
                signal: this.abortController.signal,
            });

            if (!response.ok) {
                const text = await response.text();
                handleError(
                    new Error(`Failed to create dataset: ${response.status}`),
                    text
                );
                return false;
            }

            return response.json();
        } catch (error) {
            handleError(error, 'Error creating V2 dataset.');
            return false;
        }
    }

    /**
     * Uploads one slice blob into an existing dataset.
     */
    public async uploadFileToDataset(
        request: RequestParameters,
        blob: BlobUploadType,
        datasetID: string,
        idx: number
    ): Promise<boolean> {
        try {
            const params: Record<string, string> = {};
            params.sessionId = request.sessionId ?? this.sessionID;

            const url = this.envelopeURL(`v2/images/${datasetID}`, params);
            const fileName = datasetID + '_' + idx + '.dcm';

            const formData = new FormData();
            formData.append('file', blob.file, fileName);

            const headers = {
                ...(await this.defaultHeaders()),
            };

            const response = await fetch(url, {
                method: 'PUT',
                headers,
                body: formData,
            });

            if (!response.ok) {
                const errorText = await response.text();
                handleError(
                    new Error(`Failed to upload file: ${response.status}`),
                    errorText
                );
            }

            return response.ok;
        } catch (error) {
            handleError(error, 'Error uploading file to dataset.');
            return false;
        }
    }

    /**
     * Polls `GET /v2/{taskPathSegment}/{taskId}` until the task
     * settles or the timeout fires. Returns the list of result IDs on
     * success, `false` on explicit backend error or timeout.
     */
    public async monitorTask(
        request: RequestParameters,
        taskId: string,
        pollIntervalMs: number = DEFAULT_POLL_INTERVAL_MS,
        timeoutMs: number = DEFAULT_TIMEOUT_MS
    ): Promise<{ resultIds: string[] } | false> {
        const startTime = Date.now();

        while (Date.now() - startTime < timeoutMs) {
            try {
                const task = await this.getTaskStatus(request, taskId);

                if (
                    task &&
                    task.task_status === 'PROCESSED' &&
                    Array.isArray(task.results)
                ) {
                    return { resultIds: task.results };
                }

                if (task && task.task_status === 'ERRORED') {
                    handleError(
                        new Error('Task failed on backend'),
                        JSON.stringify(task.errors)
                    );
                    return false;
                }
            } catch (error) {
                console.warn('Polling error, retrying...', error);
            }

            await new Promise((resolve) => setTimeout(resolve, pollIntervalMs));
        }

        handleError(new Error('Timeout waiting for task results'));
        return false;
    }

    /**
     * Fetches a single result blob by id.
     */
    public async fetchTaskResult(
        request: RequestParameters,
        taskId: string,
        resultId: string
    ): Promise<Blob | false> {
        try {
            const params: Record<string, string> = {};
            params.sessionId = request.sessionId ?? this.sessionID;

            const url = this.envelopeURL(
                `v2/${this.taskPathSegment}/${taskId}/results/${resultId}`,
                params
            );
            const headers = {
                ...(await this.defaultHeaders()),
                'Content-Type': 'application/json',
            };

            const response = await fetch(url, { method: 'GET', headers });
            if (!response.ok) {
                const text = await response.text();
                handleError(
                    new Error(
                        `Failed to fetch result file: ${response.status}`
                    ),
                    text
                );
                return false;
            }
            return await response.blob();
        } catch (error) {
            handleError(error, 'Error fetching result file.');
            return false;
        }
    }

    /**
     * Deletes the task and its associated results.
     */
    public async deleteTask(
        request: RequestParameters,
        taskId: string
    ): Promise<boolean> {
        try {
            const params: Record<string, string> = {};
            params.sessionId = request.sessionId ?? this.sessionID;
            const url = this.envelopeURL(
                `v2/${this.taskPathSegment}/${taskId}`,
                params
            );
            const headers = {
                ...(await this.defaultHeaders()),
                'Content-Type': 'application/json',
            };
            const response = await fetch(url, { method: 'DELETE', headers });
            if (!response.ok) {
                const text = await response.text();
                handleError(
                    new Error(
                        `Failed to delete task result: ${response.status}`
                    ),
                    text
                );
                return false;
            }
            return true;
        } catch (error) {
            handleError(error, 'Error deleting task result.');
            return false;
        }
    }

    private async getTaskStatus(request: RequestParameters, taskId: string) {
        try {
            const params: Record<string, string> = {};
            params.sessionId = request.sessionId ?? this.sessionID;

            const url = this.envelopeURL(
                `v2/${this.taskPathSegment}/${taskId}`,
                params
            );
            const headers = {
                ...(await this.defaultHeaders()),
                'Content-Type': 'application/json',
            };

            const response = await fetch(url, { method: 'GET', headers });

            if (!response.ok) {
                const text = await response.text();
                handleError(
                    new Error(
                        `Failed to fetch task status: ${response.status}`
                    ),
                    text
                );
                return false;
            }
            return await response.json();
        } catch (error) {
            handleError(error, 'Error fetching task status.');
            return false;
        }
    }
}
